import L from 'leaflet'
import { length as turfLength, area as turfArea, lineString, polygon as turfPolygon } from '@turf/turf'

// Persisted measurement attached to line/polygon features.
export function computeMeasurement(kind, geometry) {
  try {
    if (kind === 'line' && geometry?.type === 'LineString' && geometry.coordinates.length >= 2) {
      return { length_km: turfLength(lineString(geometry.coordinates), { units: 'kilometers' }) }
    }
    if ((kind === 'polygon' || kind === 'rectangle') && geometry?.type === 'Polygon') {
      return { area_m2: turfArea(turfPolygon(geometry.coordinates)) }
    }
  } catch {
    /* malformed geometry mid-edit — no measurement */
  }
  return null
}

export function formatMeasurement(m) {
  if (!m) return ''
  if (typeof m.length_km === 'number') {
    return m.length_km < 1
      ? `${Math.round(m.length_km * 1000)} m`
      : `${m.length_km.toFixed(2)} km`
  }
  if (typeof m.area_m2 === 'number') {
    return m.area_m2 < 1e6
      ? `${Math.round(m.area_m2).toLocaleString()} m²`
      : `${(m.area_m2 / 1e6).toFixed(2)} km²`
  }
  return ''
}

// Ephemeral click-to-measure tool. Not persisted to Yjs — purely a local ruler.
// Click to add points; the running distance shows at the last point. Toggling
// off (or calling clear) wipes it.
export function createMeasureTool(map, { onChange } = {}) {
  let active = false
  let points = []
  let line = null
  const vertexMarkers = []

  function clear() {
    points = []
    if (line) {
      line.remove()
      line = null
    }
    vertexMarkers.forEach((m) => m.remove())
    vertexMarkers.length = 0
  }

  function redraw() {
    const latlngs = points.map((p) => L.latLng(p[0], p[1]))
    if (!line) {
      line = L.polyline(latlngs, { color: '#0ea5e9', weight: 3, dashArray: '6 6' }).addTo(map)
    } else {
      line.setLatLngs(latlngs)
    }
    if (points.length >= 2) {
      const coords = points.map((p) => [p[1], p[0]])
      const km = turfLength(lineString(coords), { units: 'kilometers' })
      line
        .bindTooltip(formatMeasurement({ length_km: km }), { permanent: true, direction: 'top' })
        .openTooltip(latlngs[latlngs.length - 1])
    }
  }

  function onClick(e) {
    points.push([e.latlng.lat, e.latlng.lng])
    redraw()
  }

  function setActive(on) {
    if (on === active) return active
    active = on
    if (active) {
      map.on('click', onClick)
      L.DomUtil.addClass(map.getContainer(), 'measure-cursor')
    } else {
      map.off('click', onClick)
      L.DomUtil.removeClass(map.getContainer(), 'measure-cursor')
      clear()
    }
    onChange?.(active)
    return active
  }

  return {
    toggle: () => setActive(!active),
    setActive,
    clear,
    isActive: () => active,
    destroy: () => setActive(false),
  }
}
